import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { ROUTE_URLS } from '../../routes';
import { getNavSections, isNavItemActive } from '../../navigation';
import { useI18n } from '../../i18n';

export const Brand = ({ onClick }) => {
  const { t } = useI18n();
  return (
    <Link to={ROUTE_URLS.HOME} className='wm-brand' onClick={onClick} aria-label={t('app.fullName')}>
      <span className='wm-brand__mark'>W&amp;M</span>
      <span className='wm-brand__text'>
        <strong>{t('app.fullName')}</strong>
        <span>{t('app.tagline')}</span>
      </span>
    </Link>
  );
};

export const SidebarContent = ({ collapsed = false, onNavigate, onToggle }) => {
  const { isAdmin, pages } = useSelector(state => state.authReducer);
  const { pathname } = useLocation();
  const { t } = useI18n();
  const sections = useMemo(() => getNavSections(isAdmin, pages), [isAdmin, pages]);

  return (
    <div className='wm-sidebar__inner'>
      <Brand onClick={onNavigate} />
      <nav className='wm-nav' aria-label={t('nav.sectionMain')}>
        {sections.map(section => (
          <div className='wm-nav__section' key={section.key}>
            <div className='wm-nav__label'>{t(`nav.${section.key}`)}</div>
            {section.items.map(item => {
              const Icon = item.icon;
              const active = isNavItemActive(item, pathname);
              const link = (
                <Link
                  to={item.path}
                  className={`wm-nav__item${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={onNavigate}
                >
                  <Icon size={18} strokeWidth={1.75} />
                  <span className='wm-nav__text'>{t(`nav.${item.key}`)}</span>
                </Link>
              );
              return collapsed ? (
                <Tooltip key={item.key} title={t(`nav.${item.key}`)} placement='right' mouseEnterDelay={0.05}>
                  {link}
                </Tooltip>
              ) : (
                <React.Fragment key={item.key}>{link}</React.Fragment>
              );
            })}
          </div>
        ))}
      </nav>
      {onToggle && (
        <div className='wm-sidebar__footer'>
          <Tooltip title={collapsed ? t('nav.expand') : ''} placement='right'>
            <button
              type='button'
              className='wm-icon-btn wm-icon-btn--block'
              onClick={onToggle}
              aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
              aria-expanded={!collapsed}
            >
              {collapsed ? (
                <PanelLeftOpen size={18} strokeWidth={1.75} />
              ) : (
                <PanelLeftClose size={18} strokeWidth={1.75} />
              )}
              <span className='wm-nav__text'>{t('nav.collapse')}</span>
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ collapsed, onCollapse }) => (
  <aside className='wm-sidebar'>
    <SidebarContent collapsed={collapsed} onToggle={onCollapse} />
  </aside>
);
